import React, { useState } from "react"
import { Typography } from "antd"
import edit1 from "./images/edit1.jpg"
import edit2 from "./images/edit2.jpg"
import edit3 from "./images/edit3.jpg"
import picPre from "./images/pic_pre.jpeg"
import picPost from "./images/pic_post.jpeg"
import { useWindowWidth } from "./useWindowWidth"

const { Title, Text } = Typography

const steps = [
  {
    num: 1,
    img: edit1,
    title: "Upload Your Photo",
    body: [
      "Click the upload area or drag and drop any photo — JPG, PNG, TIFF, or HEIC (iPhone photos work directly).",
      "The image is never saved on our server.",
    ],
  },
  {
    num: 2,
    img: edit2,
    title: "Remove Background & Crop",
    body: [
      "Click \"Remove BG\" and let AI handle the background automatically. Use \"Crop\" to draw a box around your face, then click \"Generate\" to create your passport photo.",
      "You can use the Levels tool or eraser tools to fine-tune the image.",
    ],
  },
  {
    num: 3,
    img: edit3,
    title: "Download & Print",
    body: [
      "Click \"Make 4×6 photo paper\" to get a print-ready layout at 300 DPI, perfectly sized for cutting at any photo shop.",
      "Download the single crop or the full 4×6 sheet. Bring it to a print shop and ask for a 4×6 glossy print.",
    ],
  },
  {
    num: 4,
    title: "Remove Any Background",
    body: [
      "We can remove almost any background while preserving fine details like hair strands.",
    ],
    showcase: [
      { label: "Before", img: picPre },
      { label: "After", img: picPost },
    ],
  },
]

export default function Introduction() {
  const [expanded, setExpanded] = useState(true)
  const isMobile = useWindowWidth() < 768

  return (
    <div
      style={{
        background: "linear-gradient(180deg, #e8f0fe 0%, #f0f2f5 100%)",
        border: "1px solid #d0e0ff",
        borderRadius: 16,
        marginBottom: 28,
        overflow: "hidden",
      }}
    >
      {/* Section header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "18px 28px 16px",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Title level={5} style={{ margin: 0, color: "#1677ff", fontSize: 15, fontWeight: 700 }}>
          📖 How to use this tool
        </Title>
        <span style={{ color: "#1677ff", fontSize: 13 }}>{expanded ? "Hide ▲" : "Show ▼"}</span>
      </div>

      {expanded && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
              gap: 0,
              borderTop: "1px solid #d0e0ff",
            }}
          >
            {steps.map((step, idx) => (
              <div
                key={step.num}
                style={{
                  padding: isMobile ? "20px 18px 24px" : "28px 32px 32px",
                  borderRight: !isMobile && idx < steps.length - 1 ? "1px solid #d0e0ff" : "none",
                  borderBottom: isMobile && idx < steps.length - 1 ? "1px solid #d0e0ff" : "none",
                  display: "flex",
                  flexDirection: isMobile ? "row" : "column",
                  gap: isMobile ? 16 : 0,
                  alignItems: isMobile ? "flex-start" : "stretch",
                }}
              >
                {/* On mobile: image on the right side as thumbnail */}
                {isMobile && step.img && (
                  <div
                    style={{
                      flexShrink: 0,
                      width: 110,
                      borderRadius: 8,
                      overflow: "hidden",
                      border: "1px solid #c8d8f8",
                      boxShadow: "0 2px 8px rgba(22,119,255,0.12)",
                      background: "#fff",
                    }}
                  >
                    <img src={step.img} alt={`Step ${step.num}`} style={{ width: "100%", display: "block" }} />
                  </div>
                )}

                {/* Text content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Step number badge */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: isMobile ? 10 : 16 }}>
                    <div
                      style={{
                        width: step.num === 4 ? 39 : 26,
                        height: step.num === 4 ? 39 : 26,
                        borderRadius: "50%",
                        background: step.num === 4 ? "linear-gradient(135deg, #ff6b6b, #cf1322)" : "linear-gradient(135deg, #1677ff, #0958d9)",
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: 13,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: step.num === 4 ? "0 2px 6px rgba(207,19,34,0.35)" : "0 2px 6px rgba(22,119,255,0.35)",
                      }}
                    >
                      {step.num === 4 ? "New" : step.num}
                    </div>
                    <span style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, color: "#1a1a2e" }}>{step.title}</span>
                  </div>

                  {/* Screenshot image — desktop only (full width) */}
                  {!isMobile && step.img && (
                    <div
                      style={{
                        borderRadius: 10,
                        overflow: "hidden",
                        border: "1px solid #c8d8f8",
                        boxShadow: "0 4px 16px rgba(22,119,255,0.12)",
                        marginBottom: 20,
                        background: "#fff",
                      }}
                    >
                      <img
                        src={step.img}
                        alt={`Step ${step.num}: ${step.title}`}
                        style={{ width: "100%", display: "block", objectFit: "cover" }}
                      />
                    </div>
                  )}

                  {/* Description */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {step.body.map((line, i) => (
                      <Text key={i} style={{ fontSize: isMobile ? 13 : 13.5, color: "#444", lineHeight: 1.65 }}>
                        {line}
                      </Text>
                    ))}
                  </div>

                  {step.showcase && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        marginTop: 14,
                        ...(isMobile ? { width: "66.67%", alignSelf: "center", margin: "14px auto 0" } : {}),
                      }}
                    >
                      {step.showcase.map((item) => (
                        <div
                          key={item.label}
                          style={{
                            background: "#fff",
                            border: "1px solid #c8d8f8",
                            borderRadius: 10,
                            overflow: "hidden",
                            boxShadow: "0 3px 10px rgba(22,119,255,0.10)",
                          }}
                        >
                          <div
                            style={{
                              padding: "6px 10px",
                              borderBottom: "1px solid #e2ebff",
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#1a1a2e",
                              background: "#f8fbff",
                            }}
                          >
                            {item.label}
                          </div>
                          <img
                            src={item.img}
                            alt={`Background removal ${item.label.toLowerCase()}`}
                            style={{
                              width: "100%",
                              display: "block",
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
